// Client API REST pour Autodiag SCOP

import type { AuthStatus } from '@autodiag/shared'

// En dev, le proxy Vite redirige /api vers localhost:4000
// En prod, l'API sera servie depuis le même domaine
const API_URL = import.meta.env.VITE_API_URL || '/api'

interface ApiError {
  message: string
  status: number
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (response.status === 401) {
    window.location.href = `/api/auth/login?returnTo=${encodeURIComponent(window.location.pathname)}`
    throw { message: 'Non authentifie', status: 401 } as ApiError
  }
  if (!response.ok) {
    const error: ApiError = {
      message: `HTTP error ${response.status}`,
      status: response.status,
    }
    try {
      const data = await response.json()
      error.message = data.error || data.message || error.message
    } catch {
      // Response is not JSON
    }
    throw error
  }
  return response.json()
}

function getHeaders(): HeadersInit {
  return {
    'Content-Type': 'application/json',
  }
}

const fetchOptions: RequestInit = {
  credentials: 'include',
}

// Types pour les requêtes API - V3 Multi-métiers

export interface CreateDiagnosticRequest {
  entreprise: {
    raisonSociale: string
    siret?: string
    effectif?: number
  }
  // V3: Support multi-métiers (1-3 métiers)
  metierIds: string[]
  // Rétrocompatibilité: metierId unique (sera converti en metierIds)
  metierId?: string
}

// V3: Réponses avec valeur numérique et metierId
export interface SaveReponsesRequest {
  reponses: Array<{
    questionId: string
    metierId?: string // Pour Q1: métier concerné, null pour Q2
    valeur: number // 0-3
    commentaire?: string
  }>
}

// Types pour les plans d'action
export interface SaveActionPlanInput {
  solutionId: string
  metierCodes: string[]
  theme: string
  libelle: string
  responsable?: string | null
  dateDebut?: string | null
  dateFin?: string | null
  dateRealisation?: string | null
  statut?: 'a_faire' | 'en_cours' | 'termine'
}

// Client API
export const api = {
  // Métiers
  getMetiers: async () => {
    const response = await fetch(`${API_URL}/metiers`, {
      headers: getHeaders(),
      ...fetchOptions,
    })
    return handleResponse<any[]>(response)
  },

  getMetierByCode: async (code: string) => {
    const response = await fetch(`${API_URL}/metiers/${code}`, {
      headers: getHeaders(),
      ...fetchOptions,
    })
    return handleResponse<any>(response)
  },

  // Questions
  getQuestions: async (params?: { questionnaire?: 'Q1' | 'Q2'; axe?: string }) => {
    const searchParams = new URLSearchParams()
    if (params?.questionnaire) searchParams.append('questionnaire', params.questionnaire)
    if (params?.axe) searchParams.append('axe', params.axe)

    const url = searchParams.toString()
      ? `${API_URL}/questions?${searchParams}`
      : `${API_URL}/questions`

    const response = await fetch(url, {
      headers: getHeaders(),
      ...fetchOptions,
    })
    return handleResponse<any[]>(response)
  },

  getQuestionsQ1: async () => {
    const response = await fetch(`${API_URL}/questions/q1`, {
      headers: getHeaders(),
      ...fetchOptions,
    })
    return handleResponse<any[]>(response)
  },

  getQuestionsQ2: async () => {
    const response = await fetch(`${API_URL}/questions/q2`, {
      headers: getHeaders(),
      ...fetchOptions,
    })
    return handleResponse<any[]>(response)
  },

  getQuestionsByAxe: async (axe: string) => {
    const response = await fetch(`${API_URL}/questions/by-axe/${axe}`, {
      headers: getHeaders(),
      ...fetchOptions,
    })
    return handleResponse<any[]>(response)
  },

  getQuestionsByMetier: async (metierId: string, questionnaire?: 'Q1' | 'Q2') => {
    const searchParams = new URLSearchParams()
    if (questionnaire) searchParams.append('questionnaire', questionnaire)

    const url = searchParams.toString()
      ? `${API_URL}/questions/by-metier/${metierId}?${searchParams}`
      : `${API_URL}/questions/by-metier/${metierId}`

    const response = await fetch(url, {
      headers: getHeaders(),
      ...fetchOptions,
    })
    return handleResponse<any[]>(response)
  },

  // Solutions
  getSolutions: async (params?: { type?: string; axe?: string; priorite?: number }) => {
    const searchParams = new URLSearchParams()
    if (params?.type) searchParams.append('type', params.type)
    if (params?.axe) searchParams.append('axe', params.axe)
    if (params?.priorite) searchParams.append('priorite', params.priorite.toString())

    const url = searchParams.toString()
      ? `${API_URL}/solutions?${searchParams}`
      : `${API_URL}/solutions`

    const response = await fetch(url, {
      headers: getHeaders(),
      ...fetchOptions,
    })
    return handleResponse<any[]>(response)
  },

  getSolutionsByType: async (type: string) => {
    const response = await fetch(`${API_URL}/solutions/by-type/${type}`, {
      headers: getHeaders(),
      ...fetchOptions,
    })
    return handleResponse<any[]>(response)
  },

  getSolutionsByAxe: async (axe: string) => {
    const response = await fetch(`${API_URL}/solutions/by-axe/${axe}`, {
      headers: getHeaders(),
      ...fetchOptions,
    })
    return handleResponse<any[]>(response)
  },

  // Diagnostics
  getDiagnostics: async () => {
    const response = await fetch(`${API_URL}/diagnostics`, {
      headers: getHeaders(),
      ...fetchOptions,
    })
    return handleResponse<any[]>(response)
  },

  getDiagnostic: async (id: string) => {
    const response = await fetch(`${API_URL}/diagnostics/${id}`, {
      headers: getHeaders(),
      ...fetchOptions,
    })
    return handleResponse<any>(response)
  },

  createDiagnostic: async (data: CreateDiagnosticRequest) => {
    const response = await fetch(`${API_URL}/diagnostics`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
      ...fetchOptions,
    })
    return handleResponse<any>(response)
  },

  saveSelections: async (diagnosticId: string, data: {
    selectedThemes?: string[]
    selectedSolutions?: Array<{ solutionId: string; metierIds: string[] }>
  }) => {
    const response = await fetch(`${API_URL}/diagnostics/${diagnosticId}/selections`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify(data),
      ...fetchOptions,
    })
    return handleResponse<any>(response)
  },

  deleteDiagnostic: async (id: string) => {
    const response = await fetch(`${API_URL}/diagnostics/${id}`, {
      method: 'DELETE',
      headers: getHeaders(),
      ...fetchOptions,
    })
    return handleResponse<any>(response)
  },

  // Story 16 : renommer le diagnostic (texte libre, ≤100 chars, null pour effacer)
  renameDiagnostic: async (id: string, nom: string | null) => {
    const response = await fetch(`${API_URL}/diagnostics/${id}/nom`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify({ nom }),
      ...fetchOptions,
    })
    return handleResponse<{ id: string; nom: string | null }>(response)
  },

  renewDiagnostic: async (id: string) => {
    const response = await fetch(`${API_URL}/diagnostics/${id}/renouveler`, {
      method: 'POST',
      headers: getHeaders(),
      ...fetchOptions,
    })
    return handleResponse<any>(response)
  },

  saveReponses: async (diagnosticId: string, data: SaveReponsesRequest) => {
    const response = await fetch(`${API_URL}/diagnostics/${diagnosticId}/reponses`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(data),
      ...fetchOptions,
    })
    return handleResponse<any>(response)
  },

  terminerDiagnostic: async (diagnosticId: string) => {
    const response = await fetch(`${API_URL}/diagnostics/${diagnosticId}/terminer`, {
      method: 'POST',
      headers: getHeaders(),
      ...fetchOptions,
    })
    return handleResponse<any>(response)
  },

  // Plans d'action
  getActionPlans: async (diagnosticId: string) => {
    const response = await fetch(`${API_URL}/diagnostics/${diagnosticId}/actions`, {
      headers: getHeaders(),
      ...fetchOptions,
    })
    return handleResponse<any[]>(response)
  },

  saveActionPlans: async (diagnosticId: string, data: { actions: SaveActionPlanInput[] }) => {
    const response = await fetch(`${API_URL}/diagnostics/${diagnosticId}/actions`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(data),
      ...fetchOptions,
    })
    return handleResponse<any[]>(response)
  },

  patchActionPlan: async (diagnosticId: string, actionId: string, data: {
    responsable?: string | null
    dateDebut?: string | null
    dateFin?: string | null
    dateRealisation?: string | null
    statut?: 'a_faire' | 'en_cours' | 'termine'
  }) => {
    const response = await fetch(`${API_URL}/diagnostics/${diagnosticId}/actions/${actionId}`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify(data),
      ...fetchOptions,
    })
    return handleResponse<any>(response)
  },

  getAllActionPlans: async () => {
    const response = await fetch(`${API_URL}/action-plans`, {
      headers: getHeaders(),
      ...fetchOptions,
    })
    return handleResponse<any[]>(response)
  },

  // Attestations
  createAttestation: async (formData: FormData) => {
    const response = await fetch(`${API_URL}/attestations`, {
      method: 'POST',
      body: formData,
      ...fetchOptions,
      // Pas de Content-Type header : le navigateur gere le boundary multipart
    })
    return handleResponse<any>(response)
  },

  // Notifications (badges sidebar)
  getNotifications: async () => {
    const response = await fetch(`${API_URL}/notifications`, {
      headers: getHeaders(),
      ...fetchOptions,
    })
    return handleResponse<{ attestationPendingIds: string[]; completedDiagnosticIds: string[] }>(response)
  },

  getUserAttestations: async () => {
    const response = await fetch(`${API_URL}/attestations`, {
      headers: getHeaders(),
      ...fetchOptions,
    })
    return handleResponse<any[]>(response)
  },

  getAttestation: async (id: string) => {
    const response = await fetch(`${API_URL}/attestations/${id}`, {
      headers: getHeaders(),
      ...fetchOptions,
    })
    return handleResponse<any>(response)
  },

  // Attestation directe (1-4 casques, sans certification)
  createDirectAttestation: async (data: {
    diagnosticId: string
    genreGerant: 'M' | 'Mme'
    nomGerant: string
    prenomGerant: string
    nomEntreprise: string
    siret?: string
  }) => {
    const response = await fetch(`${API_URL}/attestations/direct`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
      ...fetchOptions,
    })
    return handleResponse<any>(response)
  },

  // Attestation PDF
  downloadAttestationPDF: async (diagnosticId: string): Promise<void> => {
    const response = await fetch(`${API_URL}/diagnostics/${diagnosticId}/attestation-pdf`, {
      ...fetchOptions,
    })

    if (response.status === 401) {
      window.location.href = `/api/auth/login?returnTo=${encodeURIComponent(window.location.pathname)}`
      return
    }

    if (!response.ok) {
      let message = `Erreur ${response.status}`
      try {
        const data = await response.json()
        message = data.error || message
      } catch { /* pas JSON */ }
      throw { message, status: response.status } as ApiError
    }

    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = response.headers.get('Content-Disposition')
      ?.match(/filename="(.+)"/)?.[1]
      || `attestation-diag-scop-${diagnosticId}.pdf`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  },

  // Rapport PDF
  downloadRapportPDF: async (diagnosticId: string): Promise<void> => {
    const response = await fetch(`${API_URL}/diagnostics/${diagnosticId}/rapport-pdf`, {
      ...fetchOptions,
    })

    if (response.status === 401) {
      window.location.href = `/api/auth/login?returnTo=${encodeURIComponent(window.location.pathname)}`
      return
    }

    if (!response.ok) {
      let message = `Erreur ${response.status}`
      try {
        const data = await response.json()
        message = data.error || message
      } catch { /* pas JSON */ }
      throw { message, status: response.status } as ApiError
    }

    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = response.headers.get('Content-Disposition')
      ?.match(/filename="(.+)"/)?.[1]
      || `rapport-synthese-${diagnosticId}.pdf`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  },

  downloadPlanActionPDF: async (diagnosticId: string): Promise<void> => {
    const response = await fetch(`${API_URL}/diagnostics/${diagnosticId}/plan-action-pdf`, {
      ...fetchOptions,
    })

    if (response.status === 401) {
      window.location.href = `/api/auth/login?returnTo=${encodeURIComponent(window.location.pathname)}`
      return
    }

    if (!response.ok) {
      let message = `Erreur ${response.status}`
      try {
        const data = await response.json()
        message = data.error || message
      } catch { /* pas JSON */ }
      throw { message, status: response.status } as ApiError
    }

    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = response.headers.get('Content-Disposition')
      ?.match(/filename="(.+)"/)?.[1]
      || `plan-action-${diagnosticId}.pdf`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  },
}

// Auth API
export const authApi = {
  getMe: async (): Promise<AuthStatus> => {
    const response = await fetch(`${API_URL}/auth/me`, fetchOptions)
    return response.json()
  },

  logout: async () => {
    const response = await fetch(`${API_URL}/auth/logout`, {
      method: 'POST',
      ...fetchOptions,
    })
    return handleResponse<{ success: boolean; redirectUrl: string }>(response)
  },

  // Story 18 : update du SIRET local pour une organisation donnée (ne remonte pas au SSO)
  updateSiret: async (organizationId: number, siret: string | null) => {
    const response = await fetch(`${API_URL}/auth/me/siret`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify({ organizationId, siret }),
      ...fetchOptions,
    })
    return handleResponse<{ siretByOrg: Record<string, string> }>(response)
  },
}

export default api
